import Controller from '@ember/controller';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import BulkActionModel, {
  type BulkActionCustomRenameCustomProperty,
  type BulkActionResponseContactIds,
} from 'donor-elf-web/models/bulk-action';
import { type PropertiesPropertyRouteModel } from 'donor-elf-web/routes/funds/fund/properties/property';
import ContactsCache, {
  type ContactCustomPropertyInfo,
  isGroupsContactCustomPropertyInfo,
} from 'donor-elf-web/services/contacts-cache';
import Settings from 'donor-elf-web/services/settings';
import { isEmpty } from '@ember/utils';

let validateNewLabel = (
  currentCustomProperties: ContactCustomPropertyInfo[],
) => {
  return {
    newLabel: (_key: string, newValue: string): string | true => {
      if (isEmpty(newValue)) {
        return 'Must be present';
      }
      let lowercaseNewLabel = newValue.toLowerCase().trim();
      if (
        currentCustomProperties.some(
          (p) => p.l.toLowerCase() === lowercaseNewLabel,
        )
      ) {
        return 'That property name already exists';
      }
      return true;
    },
  };
};

export default class PropertiesPropertyRenameController extends Controller {
  @service declare contactsCache: ContactsCache;
  @service declare settings: Settings;

  model!: PropertiesPropertyRouteModel;

  @tracked changeset?: any;
  @tracked showValidationErrors = false;

  bulkAction?: BulkActionModel;
  @tracked bulkActionComplete = false;
  @tracked saving = false;
  @tracked saveError = false;

  get isGroupsProperty() {
    return isGroupsContactCustomPropertyInfo(this.model.property);
  }

  get isReadonlyProperty() {
    return this.model.property.r === true;
  }

  @action
  templateDidInsert() {
    let validation = validateNewLabel(this.contactsCache.customProperties);
    this.changeset = new Changeset(
      { newLabel: '' },
      lookupValidator(validation),
      validation,
    );
  }

  @action
  templateWillDestroy() {
    this.showValidationErrors = false;
    this.bulkAction = undefined;
    this.bulkActionComplete = false;
    this.saving = false;
    this.saveError = false;
  }

  @action
  async save() {
    await this.changeset.validate();
    if (this.changeset.get('isValid')) {
      this.showValidationErrors = false;
      this.bulkActionComplete = false;
      this.saving = true;
      this.bulkAction = this.store.createRecord('bulk-action');
      this.bulkAction.actionType = 'rename_custom_property';
      (this.bulkAction.custom as BulkActionCustomRenameCustomProperty) = {
        property_label: this.model.property.l,
        new_label: this.changeset.get('newLabel'),
      };
      try {
        await this.bulkAction.save();
      } catch (error: any) {
        this.bulkActionComplete = true;
        this.saveError = true;
      }
    } else {
      this.showValidationErrors = true;
    }
  }

  @action
  async handleBulkActionStatusChange(bulkAction: BulkActionModel) {
    if (bulkAction.status === 'completed') {
      let response = bulkAction.response as BulkActionResponseContactIds;
      await bulkAction.destroyRecord();

      // Rename the property label in the settings
      let existingProperties =
        this.settings.fundSettings!.contactCustomInfo!.properties!;
      let originalProperty = existingProperties.find(
        (p) => p.l === this.model.property.l,
      )!;
      originalProperty.l = (
        bulkAction.custom as BulkActionCustomRenameCustomProperty
      ).new_label;
      this.settings.fundSettings!.contactCustomInfo!.properties! =
        existingProperties;
      // break the custom properties cache
      this.contactsCache.customPropertiesCacheIndex += 1;

      if (response.contact_ids.length < 250) {
        await this.contactsCache.reloadSpecificContacts(response.contact_ids);
      } else {
        await this.contactsCache.loadContacts({ forceLoad: true });
      }

      this.bulkActionComplete = true;
    } else if (bulkAction.status === 'error') {
      this.saveError = true;
      this.bulkActionComplete = true;
    }
  }
}
